import { BlockStack, Card, InlineStack, Text, Tooltip } from '@shopify/polaris'
import type { BccData } from './useBccData'
import type { ScorecardKpi } from '../../sdk/exec.api'
import { CardHeader } from './LensBadge'

// Map KPI id → display label
const KPI_LABELS: Record<string, string> = {
  'fc.net_revenue':       'Sales',
  'cf.confirmation_rate': 'Confirmation',
  'sc.shipment_rate':     'Shipping Prep',
  'fc.delivery_rate':     'Delivery',
  'cs.resolution_rate':   'Customer Service',
  'fc.return_rate':       'Returns',
}

// Which KPIs to show (in order), and whether lower is better
const HEALTH_KPIS: { id: string; lowerIsBetter?: boolean }[] = [
  { id: 'fc.net_revenue'       },
  { id: 'cf.confirmation_rate' },
  { id: 'fc.delivery_rate'     },
  { id: 'fc.return_rate',       lowerIsBetter: true },
  { id: 'cs.resolution_rate'   },
]

type HealthStatus = 'on_track' | 'at_risk' | 'off_track' | 'no_data'

function getStatus(kpi: ScorecardKpi, lowerIsBetter?: boolean): HealthStatus {
  if (kpi.status === 'on_track')  return 'on_track'
  if (kpi.status === 'at_risk')   return 'at_risk'
  if (kpi.status === 'off_track') return 'off_track'
  if (kpi.achievementPct == null) return 'no_data'
  // Fallback: derive from achievementPct
  const pct = lowerIsBetter
    ? 100 - (kpi.achievementPct - 100)   // invert for lower-is-better
    : kpi.achievementPct
  if (pct >= 90) return 'on_track'
  if (pct >= 75) return 'at_risk'
  return 'off_track'
}

const STATUS_COLOR: Record<HealthStatus, string> = {
  on_track:  'var(--p-color-bg-fill-success)',
  at_risk:   'var(--p-color-bg-fill-caution)',
  off_track: 'var(--p-color-bg-fill-critical)',
  no_data:   'var(--p-color-bg-fill-disabled)',
}

const STATUS_LABEL: Record<HealthStatus, string> = {
  on_track:  'On track',
  at_risk:   'At risk',
  off_track: 'Off track',
  no_data:   'No data',
}

type Props = { data: BccData }

export function BusinessHealth({ data }: Props) {
  const kpiMap = new Map<string, ScorecardKpi>(
    data.exec.kpiRows.map(k => [k.id, k])
  )

  const items = HEALTH_KPIS.map(({ id, lowerIsBetter }) => {
    const kpi    = kpiMap.get(id)
    const label  = KPI_LABELS[id] ?? id
    const status: HealthStatus = kpi ? getStatus(kpi, lowerIsBetter) : 'no_data'
    const tooltip = kpi
      ? `${kpi.formattedValue}${kpi.target != null ? ` · target ${kpi.target}${kpi.unit}` : ''} — ${STATUS_LABEL[status]}`
      : 'No data available'
    return { id, label, status, tooltip }
  })

  return (
    <Card>
      <BlockStack gap="300">
        <CardHeader title="Business Health" lens="performance" maturity={data.dashboard.maturity} />
        <InlineStack gap="300" wrap={false}>
          {items.map(({ id, label, status, tooltip }) => (
            <Tooltip key={id} content={tooltip}>
              <div style={{ flex: 1 }}>
                <BlockStack gap="150" inlineAlign="center">
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: STATUS_COLOR[status],
                    margin: '0 auto',
                    boxShadow: status !== 'no_data'
                      ? `0 0 6px ${STATUS_COLOR[status]}`
                      : undefined,
                  }} />
                  <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                    {label}
                  </Text>
                </BlockStack>
              </div>
            </Tooltip>
          ))}
        </InlineStack>
      </BlockStack>
    </Card>
  )
}
